import {
  ReactElement,
  ReactNode,
  cloneElement,
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Grid,
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridPagerSettings,
  GridRowProps,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';

import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { isEmpty } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { api } from 'src/assets/static/ApiRoutes';
import useViewInvoiceColumns from 'src/assets/static/view_invoice/viewInvoiceColumns';
import GridWrapper from 'src/components/GridWrapper';
import Layout from 'src/components/Layout';
import Modal from 'src/components/Modal';
import PreviewSection from 'src/components/PreviewSection';
import SelectCompany from 'src/components/SelectCompany';
import Button from 'src/components/design-system/Button';
import { ThreeWayMatchingPermissions } from 'src/components/guards/PermissionsList';
import useLinkControl from 'src/components/useLinkControl';
import useAuth from 'src/hooks/useAuth';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import useLocalStorage from 'src/hooks/useLocalstorage';
import usePermission from 'src/hooks/usePermissions';
import useRedirect from 'src/hooks/useRedirect';
import useSplitter from 'src/hooks/useSplitter';
import useViewInvoices from 'src/hooks/useViewInvoices';
import useWindowSize from 'src/hooks/useWindowSize';
import ActionButtons from 'src/sections/view_invoices/ActionButtons';
import ClosePoQtyErrorWizard from 'src/sections/view_invoices/ClosePoQtyErrorWizard';
import GLCodeMismatchWizard from 'src/sections/view_invoices/GLCodeMismatchWizard';
import LineItemSummariesWizard from 'src/sections/view_invoices/LineItemSummariesWizard';
import PercentRecDolWizard from 'src/sections/view_invoices/PercentRecDolWizard';
import PercentRecQtyWizard from 'src/sections/view_invoices/PercentRecQtyWizard';
import useClosePoApi from 'src/services/useClosePOApi';
import useCreateInvoiceApi, { CreateInvoiceResponse } from 'src/services/useCreateInvoiceApi';
import { IInvoiceListItem } from 'src/types/Invoice';
import axios from 'src/utils/axios';
import exportToExcel from 'src/utils/exportToExcel';
import { modifyRequest } from 'src/utils/requestInterceptor';
import { formatViewInvoices } from 'src/utils/transformation';
import GridToolbar from './GridToolBar';
import { viewInvoiceFilters } from './viewInvoiceFilters';

const getInitialInvoiceDataState = (
  status?: string
): State & { skipCalculatingTotalRecordsCount: boolean } => {
  let initialFilters: Array<CompositeFilterDescriptor> = [];

  // Add conditional filters based on status
  if (status === 'InvoicesToComplete') {
    initialFilters = [
      ...initialFilters,
      {
        logic: 'and',
        filters: [{ field: 'status', value: 'In Progress', operator: 'contains' }],
      },
    ];
  } else if (status === 'POsWithoutInvoices') {
    initialFilters = [
      ...initialFilters,
      {
        logic: 'and',
        filters: [{ field: 'invoiceNumber', value: 'Create New', operator: 'eq' }],
      },
      {
        logic: 'and',
        filters: [{ field: 'receiptId', value: '', operator: 'isNull' }],
      },
    ];
  } else if (status === 'POsAwaitingClosure') {
    initialFilters = [
      ...initialFilters,
      {
        logic: 'and',
        filters: [{ field: 'poStatus', value: 'Open', operator: 'eq' }],
      },
    ];
  }

  return {
    skip: 0,
    take: 20,
    skipCalculatingTotalRecordsCount: false,
    sort: [
      {
        field: 'statusDate',
        dir: 'desc',
      },
    ],
    filter: {
      logic: 'and',
      filters: initialFilters,
    },
  };
};

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'multiple',
};

const Invoice_STATUS_FIELD = 'statusDate';
const PO_DATE_FIELD = 'poDate';
const dateArr = [Invoice_STATUS_FIELD, PO_DATE_FIELD];

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const defaultProps = {
  excelFileName: 'View Invoice.xlsx',
  tableTextId: 'Invoices/Credits',
  DataItemKey: 'receiptId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

interface IModalInfo {
  title: string;
  visibility: boolean;
  content: ReactNode;
  height?: string;
}

const invoiceSelectionFilterInitialState = {
  value: 0,
  label: 'Show All Active',
};

const strFields = [
  'poNum',
  'invoiceNumber',
  'status',
  'supplierName',
  'poStatus',
  'hasGlMismatchItems',
  'poItemsCompleted',
  'poSubject',
  'buyerName',
];

function ViewInvoiceTable() {
  const [searchParams] = useSearchParams(); //use this
  const status = useMemo(() => searchParams.get('status') || '', [searchParams]); //use this
  const { getUrl, redirect } = useRedirect();
  const { height } = useWindowSize();
  const tableRef = createRef<Grid>();
  const { company: selectedCompany, changeCompany: setSelectedCompany } = useCompany();
  const { companies, user } = useAuth();
  const [dropdownFilter, setDropdownFilter] = useLocalStorage(
    'viewInvoiceFilterId',
    invoiceSelectionFilterInitialState
  );

  const [refresh, setRefresh] = useState(false);
  const { confirm, onClose } = useConfirm();
  const { closePo } = useClosePoApi();
  const [invoiceDataState, setInvoiceDataState] = useState<State>(() =>
    getInitialInvoiceDataState(status)
  );
  const [rows, setRows] = useState<IInvoiceListItem[]>([]);
  const { completeApiResponseData } = useViewInvoices();
  const [count, setCount] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const isThreeWayMatching: boolean = usePermission({ permissions: ThreeWayMatchingPermissions });
  const togglePreview = useCallback(() => setShowPreview((prev) => !prev), []);
  const [modalInfo, setModalInfo] = useState<IModalInfo>({
    title: '',
    visibility: false,
    content: '',
  });
  const viewInvoiceColumns = useViewInvoiceColumns(tableRef, rows);
  const selectedRows = useMemo(() => rows?.filter((row) => row.selected), [rows]);
  const refreshList = useCallback(() => {
    setRefresh((prev) => !prev);
  }, []);
  const previewDocument = useMemo(() => rows.find((row) => row.preview), [rows]);
  const { createInvoice } = useCreateInvoiceApi();
  const { LinkControl, openNewTab } = useLinkControl('ViewInvoice');

  const listCompanyInvoices = useCallback(
    (companyId: string, clientSide: boolean = false) => {
      const data = {
        ...modifyRequest(invoiceDataState, dateArr, undefined, strFields),
        clientSide,
      };
      data.filters = [
        ...data.filters,
        ...(viewInvoiceFilters[dropdownFilter.value]?.filters || []),
      ];
      data.sort = [...data.sort, ...(viewInvoiceFilters[dropdownFilter.value]?.sort || [])];

      return axios.post(api.viewInvoicesUrlFn(companyId), data);
    },
    [dropdownFilter.value, invoiceDataState]
  );

  const excelExport = useCallback(
    (data: IInvoiceListItem[]) => {
      const columns: string[] = [];
      const columnMapping: Record<string, string> = {};
      const invoiceColumns = [...viewInvoiceColumns]
        .concat([{ field: 'companyName', title: 'Company Name' }])
        .filter(
          (i) => !['selected', 'numOfAttachments', 'closePoError'].includes(i.field as string)
        );
      invoiceColumns.forEach((item) => {
        columns.push(item.field as string);
        Object.assign(columnMapping, { [item.field as string]: item.title });
      });
      const rows = data.map((item) => ({
        ...item,
        percentRecvByQuantity: `${
          item.percentRecvByQuantity === 'N/A' ? 'N/A' : `${item.percentRecvByQuantity}%`
        }`,
        percentRecvByDollar: `${
          item.percentRecvByDollar === 'N/A' ? 'N/A' : `${item.percentRecvByDollar}%`
        }`,
        docTotal: `$${item.docTotal}`,
        currentDocTotal: `$${item.currentDocTotal}`,
      }));
      exportToExcel({
        columns,
        rows,
        fileName: 'view-invoice',
        columnMapping,
      });
    },
    [viewInvoiceColumns]
  );

  const handleClearFilter = useCallback(() => {
    setDropdownFilter(invoiceSelectionFilterInitialState);
    setInvoiceDataState(getInitialInvoiceDataState());
  }, [setDropdownFilter]);

  const onInvoiceDataStateChange = (e: GridDataStateChangeEvent) => {
    setInvoiceDataState(e.dataState);
  };

  const onInvoiceSelect = useCallback((event: GridSelectionChangeEvent) => {
    setRows((prev) => {
      const prevItems = [...prev];
      const index = event?.startRowIndex;
      prevItems[index].selected = !prevItems[index]?.selected;
      return prevItems;
    });
  }, []);

  const onHeaderSelectionChange = useCallback((event: GridHeaderSelectionChangeEvent) => {
    const checkboxElement: any = event?.syntheticEvent?.target;
    const { checked } = checkboxElement;
    setRows((prev) => prev?.map((row) => ({ ...row, selected: checked })));
  }, []);

  useEffect(() => {
    if (selectedCompany.companyId) {
      setLoading(true);
      listCompanyInvoices(selectedCompany.companyId)
        .then((data) => {
          const { items = [], totalRecords = 0 } = data?.data;
          setRows((prev) => formatViewInvoices(items, prev));
          setCount(totalRecords || 0);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [listCompanyInvoices, selectedCompany.companyId, refresh]);

  //use this to select a company automatically
  useEffect(() => {
    if (status && companies.length > 0) {
      const company = companies.find((company) => company.companyId === user.CompanyId);
      if (company) {
        setSelectedCompany(company);
      }
    }
  }, [companies, status, user.CompanyId, setSelectedCompany]);

  // Update invoiceDataState when status changes
  useEffect(() => {
    setInvoiceDataState(getInitialInvoiceDataState(status));
    setDropdownFilter({ value: 1, label: 'Show All' });
  }, [setDropdownFilter, status]);

  const isClearFiltersDisabled = useMemo(
    () => !invoiceDataState.filter?.filters?.length,
    [invoiceDataState.filter?.filters?.length]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <GridToolbar
        setState={setInvoiceDataState}
        setLoading={setLoading}
        clearFilter={handleClearFilter}
        selectionFilter={dropdownFilter}
        setSelectionFilter={setDropdownFilter}
        excelExport={excelExport}
        dateArr={dateArr}
        invoiceDataState={invoiceDataState}
        rows={rows}
        listCompanyInvoices={listCompanyInvoices}
        isClearFiltersDisabled={isClearFiltersDisabled}
      />
    ),
    [
      dropdownFilter,
      excelExport,
      handleClearFilter,
      invoiceDataState,
      isClearFiltersDisabled,
      listCompanyInvoices,
      rows,
      setDropdownFilter,
    ]
  );

  const unProcessedIds = useMemo(
    () => completeApiResponseData?.unProcessed?.map((value) => value?.id),
    [completeApiResponseData?.unProcessed]
  );

  const rowRender = useCallback(
    (trElement: ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
      const invoiceId = props?.dataItem?.receiptId;
      const red = { backgroundColor: '#EFB2B2' };
      let trProps: any = { ...trElement.props };
      if (!isEmpty(unProcessedIds)) {
        if (unProcessedIds?.includes(invoiceId)) trProps = { style: red };
      }
      if (previewDocument && invoiceId === previewDocument?.receiptId) {
        trProps = {
          ...trProps,
          className: `${trElement.props.className} ${props.className} ${
            props.dataItem.preview ? 'border-preview-box' : ''
          }`,
        };
      }
      return cloneElement(trElement, { ...trProps });
    },
    [previewDocument, unProcessedIds]
  );
  const onCreateNewClick = useCallback(
    async (dataItem: any) => {
      if (dataItem.isPoMpa) {
        confirm({
          title: 'Multiple Period Allocation',
          content:
            'The PO associated with this Invoice is assigned to Multiple Periods. Please choose either Multiple or Single Period Assignment for this Invoice.',
          cancelButtonText: 'Assign Single Period',
          confirmButtonText: 'Assign Multiple Periods',
          cancelColor: 'info',
          confirmColor: 'info',
          width: '500px',
          closeIcon: true,
          onClose,
        }).then(async (useMultiplePeriod: boolean) => {
          const response: CreateInvoiceResponse = await createInvoice({
            companyId: selectedCompany.companyId,
            poId: dataItem.poId,
            useSinglePeriod: !useMultiplePeriod,
          });
          redirect(
            getUrl('invoiceDetails')(response.invoiceId.toString(), dataItem.poId.toString()),
            false,
            openNewTab
          );
        });
      } else {
        const response: CreateInvoiceResponse = await createInvoice({
          companyId: selectedCompany.companyId,
          poId: dataItem.poId,
          useSinglePeriod: true,
        });
        redirect(
          getUrl('invoiceDetails')(response.invoiceId.toString(), dataItem.poId.toString()),
          false,
          openNewTab
        );
      }
    },
    [confirm, createInvoice, getUrl, onClose, openNewTab, redirect, selectedCompany.companyId]
  );

  const onPercentReceivedDollarClick = useCallback((dataItem: IInvoiceListItem) => {
    setModalInfo({
      title: '% Invoiced by Dollar Amount',
      visibility: true,
      content: <PercentRecDolWizard dataSource={dataItem} />,
    });
  }, []);

  const onPercentReceivedQtyClick = useCallback((dataItem: IInvoiceListItem) => {
    setModalInfo({
      title: '% Received by Quantity',
      visibility: true,
      content: <PercentRecQtyWizard dataSource={dataItem} />,
    });
  }, []);

  const onGLCodeMismatchClick = useCallback((dataItem: IInvoiceListItem) => {
    const { receiptId, invoiceNumber, poNum } = dataItem;
    setModalInfo({
      title: 'GL Codes',
      visibility: true,
      content: (
        <GLCodeMismatchWizard
          invoiceId={receiptId?.toString() || ''}
          invoiceNumber={invoiceNumber}
          poNum={poNum}
        />
      ),
    });
  }, []);

  const onClosePoErrorQtyClick = useCallback(
    (dataItem: IInvoiceListItem) => {
      confirm({
        title: 'Confirm PO Close',
        content: <ClosePoQtyErrorWizard poIds={[dataItem.poId]} />,
        cancelButtonText: 'Close',
        cancelColor: 'error',
        confirmButtonText: 'Confirm and Close',
        confirmColor: 'primary',
        width: '1060px',
      })
        .then(async (isClosePoApproved) => {
          if (isClosePoApproved) {
            try {
              await closePo({ poIds: [dataItem.poId] });
            } finally {
              refreshList();
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    },
    [closePo, confirm, refreshList]
  );

  const onLineItemSummariesClick = useCallback(
    (dataItem: IInvoiceListItem) => {
      setModalInfo({
        title: `PO Items ${isThreeWayMatching ? 'Received' : 'Invoiced'}`,
        visibility: true,
        content: <LineItemSummariesWizard poId={dataItem.poId} />,
      });
    },
    [isThreeWayMatching]
  );

  const onAttachmentClick = useCallback((dataItem: IInvoiceListItem) => {
    setRows((prev) =>
      [...prev].map((item) => ({
        ...item,
        ...(item?.rowId === dataItem?.rowId ? { preview: true } : { preview: false }),
      }))
    );
    setShowPreview(true);
  }, []);

  const cellClickFns = useCallback(
    () => ({
      onCreateNewClick,
      onPercentReceivedDollarClick,
      onPercentReceivedQtyClick,
      onGLCodeMismatchClick,
      onClosePoErrorQtyClick,
      onLineItemSummariesClick,
      onAttachmentClick,
      openNewTab,
    }),
    [
      onCreateNewClick,
      onPercentReceivedDollarClick,
      onPercentReceivedQtyClick,
      onGLCodeMismatchClick,
      onClosePoErrorQtyClick,
      onLineItemSummariesClick,
      onAttachmentClick,
      openNewTab,
    ]
  );

  const modal = useMemo(
    () => (
      <Modal
        className={'view-invoice-modal'}
        height={modalInfo?.height}
        title={modalInfo?.title}
        visible={modalInfo?.visibility}
        closeModal={() =>
          setModalInfo((currentModal) => ({
            ...currentModal,
            visibility: !currentModal?.visibility,
          }))
        }
      >
        {modalInfo?.content}
      </Modal>
    ),
    [modalInfo]
  );

  const table = useMemo(
    () => (
      <GridWrapper
        id="viewInvoiceTable"
        height={`${height - 92}px`}
        loading={loading}
        ref={tableRef}
        rowRender={rowRender}
        columns={viewInvoiceColumns}
        dataSource={rows}
        dataState={invoiceDataState}
        total={count}
        fieldModifiable
        onDataStateChange={onInvoiceDataStateChange}
        onSelectionChange={onInvoiceSelect}
        onHeaderSelectionChange={onHeaderSelectionChange}
        gridToolbarChildren={gridToolbarChildren}
        cellClick={cellClickFns}
        {...defaultProps}
      />
    ),
    [
      cellClickFns,
      count,
      gridToolbarChildren,
      height,
      invoiceDataState,
      loading,
      onHeaderSelectionChange,
      onInvoiceSelect,
      rowRender,
      rows,
      tableRef,
      viewInvoiceColumns,
    ]
  );

  const preview = useMemo(
    () => (
      <PreviewSection
        receiptId={previewDocument?.receiptId as number}
        status={previewDocument?.status as string}
        poStatus={previewDocument?.poStatus as string}
        isInvoice
        totalDocCount={previewDocument?.numOfAttachments}
        multiSelect={false}
        companyId={parseInt(selectedCompany?.companyId)}
        refresh={refreshList}
        ocrGenerated={previewDocument?.ocrGenerated}
      />
    ),
    [
      previewDocument?.numOfAttachments,
      previewDocument?.ocrGenerated,
      previewDocument?.poStatus,
      previewDocument?.receiptId,
      previewDocument?.status,
      refreshList,
      selectedCompany?.companyId,
    ]
  );

  const fullPage = useSplitter(table, preview, showPreview);

  return (
    <Layout>
      <div className="flex justify-between flex-wrap">
        <div className="flex">
          <SelectCompany
            className="items-center"
            onChange={handleClearFilter}
            selectSuperAdmin={status ? true : false}
          />
        </div>

        {!!selectedCompany?.companyId && (
          <>
            <ActionButtons selectedRows={selectedRows} setRows={setRows} refresh={refreshList} />
            <div className="flex items-center">
              <div className="mr-2">
                <LinkControl />
              </div>
              <Button color="light" onClick={togglePreview}>
                {showPreview ? 'Hide Scans' : 'Show Scans'}
              </Button>
            </div>
          </>
        )}
      </div>

      {fullPage}
      {modal}
    </Layout>
  );
}

export default ViewInvoiceTable;
